import React, { useEffect } from 'react';
import {
  Box,
  Controller,
  ControllerWrapper,
  DateInput,
  FieldWrapper,
  Hint,
  Label,
  SelectDropdown,
  Text,
  useFormContext,
  useWatch,
} from '@common-fe/common-fe';

import { Option } from '@/common/types';
import {
  EnrollmentFormField,
  EnrollmentFormFields,
} from '@/modules/employee/Employee/EnrollmentModal/hooks/useEnrollmentForm';

import { ACTIVE_STATUS_KEY, CANCELLED_STATUS_KEY } from '../enrollmentModal.types';

const EnrollmentStatusOptions = [
  { key: ACTIVE_STATUS_KEY, title: 'Active', value: 'Active' },
  { key: CANCELLED_STATUS_KEY, title: 'Cancel', value: 'Cancel' },
];

interface PlanFieldsProps {
  isEdit?: boolean;
  healthPlanCoverageOptions?: Option[];
  defaultHealthPlanCoverageLevel?: string;
  onChangeDateChange?: () => void;
}
const HealthPlanFields: React.FC<PlanFieldsProps> = ({
  isEdit,
  healthPlanCoverageOptions,
  defaultHealthPlanCoverageLevel,
  onChangeDateChange,
}) => {
  const { control, setValue } = useFormContext<EnrollmentFormFields>();
  const healthPlanEnrollmentStatus = useWatch({
    control,
    name: EnrollmentFormField.healthPlanEnrollmentStatus,
  });
  const healthPlanCoverageLevel = useWatch({ control, name: EnrollmentFormField.healthPlanCoverageLevel });

  useEffect(() => {
    if (
      healthPlanEnrollmentStatus === CANCELLED_STATUS_KEY &&
      healthPlanCoverageLevel !== defaultHealthPlanCoverageLevel
    ) {
      setValue(EnrollmentFormField.healthPlanCoverageLevel, defaultHealthPlanCoverageLevel);
    }
  }, [healthPlanEnrollmentStatus, healthPlanCoverageLevel, defaultHealthPlanCoverageLevel, setValue]);

  return (
    <Box gap="spacing16">
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="health-plan-year-wrapper">
        <Label name={EnrollmentFormField.healthPlanYear} isNarrow data-testid="health-plan-year-label">
          Plan Year
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="health-plan-year-controller">
          <Controller
            name={EnrollmentFormField.healthPlanYear}
            control={control}
            render={({ field, fieldState }) => (
              <SelectDropdown
                id="planYear"
                errorText={fieldState.error?.message}
                name={EnrollmentFormField.healthPlanYear}
                options={
                  field?.value
                    ? [
                        {
                          key: field?.value,
                          value: field?.value,
                          title: field?.value,
                        },
                      ]
                    : []
                }
                singleMode
                values={field?.value ? [field.value] : ['']}
                placeholder="Plan year"
                inputStyle={{ minWidth: '320px', maxWidth: '320px' }}
                disabled
                data-testid="health-plan-year-select"
              />
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="health-plan-start-date-wrapper">
        <Label
          name={EnrollmentFormField.healthPlanStartDate}
          isNarrow
          data-testid="health-plan-start-date-label"
        >
          Plan Start Date
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="health-plan-start-date-controller">
          <Controller
            name={EnrollmentFormField.healthPlanStartDate}
            control={control}
            render={({ field, fieldState }) => (
              <Box width="320px">
                <DateInput
                  errorText={fieldState.error?.message}
                  isPicker
                  isWide
                  testId="health_plan_start_date"
                  placeholder="Plan start date"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  disabled
                  data-testid="health-plan-start-date-input"
                />
              </Box>
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper style={{ justifyContent: 'space-between' }} data-testid="health-plan-end-date-wrapper">
        <Label name={EnrollmentFormField.healthPlanEndDate} isNarrow data-testid="health-plan-end-date-label">
          Plan End Date
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="health-plan-end-date-controller">
          <Controller
            name={EnrollmentFormField.healthPlanEndDate}
            control={control}
            render={({ field, fieldState }) => (
              <Box width="320px">
                <DateInput
                  errorText={fieldState.error?.message}
                  isPicker
                  isWide
                  testId="plan_end_date"
                  placeholder="Plan end date"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  disabled
                  data-testid="health-plan-end-date-input"
                />
              </Box>
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper
        style={{ justifyContent: 'space-between' }}
        data-testid="health-plan-enrollment-status-wrapper"
      >
        <Label
          showRequireIcon
          name={EnrollmentFormField.healthPlanEnrollmentStatus}
          isNarrow
          data-testid="health-plan-enrollment-status-label"
        >
          Enrollment Status
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="health-plan-enrollment-status-controller">
          <Controller
            name={EnrollmentFormField.healthPlanEnrollmentStatus}
            control={control}
            render={({ field, fieldState }) => (
              <SelectDropdown
                id="enrollmentStatus"
                name={EnrollmentFormField.healthPlanEnrollmentStatus}
                options={EnrollmentStatusOptions}
                singleMode
                values={field?.value ? [field.value] : ['']}
                placeholder="Enrollment status"
                disabled={!isEdit}
                onChange={field.onChange}
                errorText={fieldState.error?.message}
                inputStyle={{ minWidth: '320px', maxWidth: '320px' }}
                data-testid="health-plan-enrollment-status-select"
              />
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper
        style={{ justifyContent: 'space-between' }}
        data-testid="health-plan-coverage-level-wrapper"
      >
        <Label
          showRequireIcon
          name={EnrollmentFormField.healthPlanCoverageLevel}
          isNarrow
          data-testid="health-plan-coverage-level-label"
        >
          Coverage Level
        </Label>
        <ControllerWrapper style={{ flex: 'none' }} data-testid="health-plan-coverage-level-controller">
          <Controller
            name={EnrollmentFormField.healthPlanCoverageLevel}
            control={control}
            render={({ field, fieldState }) => (
              <Hint
                hideHint={healthPlanEnrollmentStatus !== CANCELLED_STATUS_KEY}
                hintAlign="right"
                hintElement={
                  <SelectDropdown
                    id="healthPlanCoverage"
                    name={EnrollmentFormField.healthPlanCoverageLevel}
                    errorText={fieldState.error?.message}
                    options={healthPlanCoverageOptions || []}
                    singleMode
                    values={field?.value ? [field.value] : ['']}
                    placeholder="Coverage Level"
                    onChange={field.onChange}
                    inputStyle={{ minWidth: '320px', maxWidth: '320px' }}
                    disabled={healthPlanEnrollmentStatus === CANCELLED_STATUS_KEY}
                    data-testid="health-plan-coverage-level-select"
                  />
                }
              >
                <Text color="textOnColor" textAlign="center" data-testid="health-plan-coverage-level-hint">
                  Coverage level can be changed only for active enrollment
                </Text>
              </Hint>
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
      <FieldWrapper
        style={{ justifyContent: 'space-between' }}
        data-testid="health-plan-enrollment-date-wrapper"
      >
        <Label
          showRequireIcon
          name={EnrollmentFormField.healthPlanEnrollmentDate}
          isNarrow
          data-testid="health-plan-enrollment-date-label"
        >
          <Box>
            <Text margin={{ bottom: 'xsmall' }} weight="bold" data-testid="health-plan-enrollment-date-title">
              Change date
            </Text>
            <Box>
              <Text
                size="small"
                weight="normal"
                style={{ maxWidth: '220px' }}
                color="textSecondary"
                data-testid="health-plan-enrollment-date-helper"
              >
                This is the date these changes become effective
              </Text>
            </Box>
          </Box>
        </Label>
        <ControllerWrapper
          style={{ flex: 'none', alignSelf: 'flex-start' }}
          data-testid="health-plan-enrollment-date-controller"
        >
          <Controller
            name={EnrollmentFormField.healthPlanEnrollmentDate}
            control={control}
            render={({ field, fieldState }) => (
              <Box width="320px">
                <DateInput
                  isPicker
                  errorText={fieldState.error?.message}
                  isWide
                  testId="enrollment_date"
                  placeholder="Change date"
                  value={field.value ?? ''}
                  onChange={(value: string) => {
                    field.onChange(value);
                    onChangeDateChange?.();
                  }}
                  data-testid="health-plan-enrollment-date-input"
                />
              </Box>
            )}
          />
        </ControllerWrapper>
      </FieldWrapper>
    </Box>
  );
};

export default React.memo(HealthPlanFields);
